import { useMemo } from "react";
import { UserRole } from "../services/UserService";
import { canPerformAction } from "../utils/rolesData";
import {
  isAdminRole,
  isPrivilegedRole,
  hasRoleOrHigher,
  getRoleLevel,
} from "../utils/RoleUtils";

/**
 * Hook to check role permissions and provide role utilities
 * @param userRole - The user's current role
 * @returns Object with permission checks and role utilities
 */
export const useRolePermissions = (userRole: UserRole | undefined) => {
  const permissions = useMemo(() => {
    if (!userRole) {
      return {
        canViewAgents: false,
        canConfigureSettings: false,
        canEnableAgents: false,
        canDisableAgents: false,
        canPauseAgents: false,
        canResumeAgents: false,
        canUninstallAgents: false,
        canManageBilling: false,
        canPerformAdminOps: false,
        canAddMembers: false,
        canRemoveMembers: false,
        canChangeRoles: false,
        canTransferOwnership: false,
        canDeleteOrg: false,
        canAccessAllOrgs: false,
        isAdmin: false,
        isPrivileged: false,
        roleLevel: 0,
      };
    }

    const roleId = userRole.toLowerCase();

    return {
      canViewAgents: canPerformAction(roleId, "view"),
      canConfigureSettings: canPerformAction(roleId, "configure"),
      canEnableAgents: canPerformAction(roleId, "enable"),
      canDisableAgents: canPerformAction(roleId, "disable"),
      canPauseAgents: canPerformAction(roleId, "pause"),
      canResumeAgents: canPerformAction(roleId, "resume"),
      canUninstallAgents: canPerformAction(roleId, "uninstall"),
      canManageBilling: canPerformAction(roleId, "billing"),
      canPerformAdminOps: canPerformAction(roleId, "admin"),
      canAddMembers: canPerformAction(roleId, "add_members"),
      canRemoveMembers: canPerformAction(roleId, "remove_members"),
      canChangeRoles: canPerformAction(roleId, "change_roles"),
      canTransferOwnership: canPerformAction(roleId, "transfer_ownership"),
      canDeleteOrg: canPerformAction(roleId, "delete_org"),
      canAccessAllOrgs: canPerformAction(roleId, "access_all_orgs"),
      isAdmin: isAdminRole(userRole),
      isPrivileged: isPrivilegedRole(userRole),
      roleLevel: getRoleLevel(userRole),
      hasRoleOrHigher: (otherRole: UserRole) => hasRoleOrHigher(userRole, otherRole),
    };
  }, [userRole]);

  return permissions;
};

